import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Shield, Upload, FileText, Clock, LogOut, User, 
  ChevronRight, CheckCircle, AlertCircle, Timer,
  Calculator, BookOpen, Calendar, Heart, MessageSquare, TrendingUp, PenLine, Settings,
  Share2, Sparkles, PieChart, CreditCard, Home, Car, GraduationCap, Banknote, AlertTriangle, FileWarning, Search, HelpCircle, Printer
} from 'lucide-react';
import axios from 'axios';
import AppFooter from '../components/AppFooter';
import ShareProgressCard from '../components/ShareProgressCard';
import InstallPromptCard from '../components/InstallPromptCard';
import PasskeyEnrollmentPrompt from '../components/PasskeyEnrollmentPrompt';
import PushReminderCard from '../components/PushReminderCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Rotating pool of encouragement verses — picked fresh each dashboard visit
// so returning users don't see the same line twice in a row.
const ENCOURAGEMENT_VERSES = [
  { text: "The Lord is close to the brokenhearted and saves those who are crushed in spirit.", ref: "Psalm 34:18" },
  { text: "See, I am doing a new thing! Now it springs up; do you not perceive it?", ref: "Isaiah 43:19" },
  { text: "For I know the plans I have for you — plans to prosper you and not to harm you, plans to give you hope and a future.", ref: "Jeremiah 29:11" },
  { text: "Commit to the Lord whatever you do, and he will establish your plans.", ref: "Proverbs 16:3" },
  { text: "The rich rule over the poor, and the borrower is slave to the lender.", ref: "Proverbs 22:7" },
  { text: "Cast all your anxiety on him because he cares for you.", ref: "1 Peter 5:7" },
  { text: "Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you wherever you go.", ref: "Joshua 1:9" },
  { text: "And my God will meet all your needs according to the riches of his glory in Christ Jesus.", ref: "Philippians 4:19" },
  { text: "Let us not become weary in doing good, for at the proper time we will reap a harvest if we do not give up.", ref: "Galatians 6:9" },
  { text: "Trust in the Lord with all your heart and lean not on your own understanding.", ref: "Proverbs 3:5" },
  { text: "He has made everything beautiful in its time.", ref: "Ecclesiastes 3:11" },
  { text: "The Lord will fight for you; you need only to be still.", ref: "Exodus 14:14" },
  { text: "Weeping may stay for the night, but rejoicing comes in the morning.", ref: "Psalm 30:5" },
  { text: "I can do all this through him who gives me strength.", ref: "Philippians 4:13" },
  { text: "Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God.", ref: "Philippians 4:6" },
  { text: "The name of the Lord is a fortified tower; the righteous run to it and are safe.", ref: "Proverbs 18:10" },
  { text: "Those who hope in the Lord will renew their strength. They will soar on wings like eagles.", ref: "Isaiah 40:31" },
  { text: "He restores my soul. He leads me in paths of righteousness for his name's sake.", ref: "Psalm 23:3" },
  { text: "Give, and it will be given to you. A good measure, pressed down, shaken together and running over, will be poured into your lap.", ref: "Luke 6:38" },
  { text: "For nothing will be impossible with God.", ref: "Luke 1:37" },
];

function pickEncouragementVerse(seed) {
  // Seed changes per page mount AND per user, so two users on the same day
  // see different verses, and a refresh also rotates.
  const pool = ENCOURAGEMENT_VERSES;
  const idx = Math.abs(seed) % pool.length;
  return pool[idx];
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [creditMix, setCreditMix] = useState(null);
  // Pick a verse on mount — changes each dashboard visit, deterministic per visit
  const [verse] = useState(() =>
    pickEncouragementVerse(Date.now() + Math.floor(Math.random() * 1000))
  );

  useEffect(() => {
    fetchDisputes();
    fetchCreditMix();
  }, []);

  const fetchCreditMix = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/credit-mix`, { withCredentials: true });
      setCreditMix(data);
    } catch (e) {
      // Silent — card simply hides. Log for debug visibility.
      console.warn('[Dashboard] Credit mix fetch failed:', e?.message);
    }
  };

  const fetchDisputes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/disputes`, {
        withCredentials: true
      });
      setDisputes(data);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const profileComplete = user?.name && user?.address && user?.ssn_last4;
  const profileProgress = [user?.name, user?.address, user?.ssn_last4].filter(Boolean).length / 3 * 100;

  const activeDisputes = disputes.filter(d => d.status === 'mailed');
  const pendingDisputes = disputes.filter(d => d.status === 'generated');
  const resolvedDisputes = disputes.filter(d => d.status === 'resolved');

  // Aggregate stats for the share card
  const itemsCleared = resolvedDisputes.reduce((sum, d) => sum + (d.items?.length || 0), 0);
  const bureausEngaged = new Set(disputes.map(d => d.bureau).filter(Boolean)).size;
  const earliest = disputes
    .map(d => (d.created_at ? new Date(d.created_at).getTime() : null))
    .filter(Boolean)
    .sort((a, b) => a - b)[0];
  const daysOnJourney = earliest
    ? Math.max(1, Math.floor((Date.now() - earliest) / (1000 * 60 * 60 * 24)))
    : 0;

  const shareStats = {
    itemsCleared,
    disputesFiled: disputes.length,
    bureausEngaged,
    daysOnJourney,
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="dashboard-page">
      <PasskeyEnrollmentPrompt user={user} />
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/dashboard"
              className="flex items-center hover:opacity-80 transition-opacity"
              data-testid="header-brand-link"
            >
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </Link>

            {/* In-app tab nav — keeps the dashboard focused, everything else is one tap away */}
            <nav className="hidden md:flex items-center gap-1" data-testid="header-tabs">
              <Link
                to="/dashboard"
                className="px-3 py-1.5 text-sm font-['Manrope'] text-[#36454F]/75 hover:text-[#36454F] hover:bg-[#36454F]/5 rounded-sm transition-colors"
                data-testid="tab-dashboard"
              >
                Dashboard
              </Link>
              <Link
                to="/budget"
                className="px-3 py-1.5 text-sm font-['Manrope'] text-[#36454F]/75 hover:text-[#B76E79] hover:bg-[#B76E79]/5 rounded-sm transition-colors"
                data-testid="tab-family-budget"
              >
                Family Budget
              </Link>
              <Link
                to="/case-files"
                className="px-3 py-1.5 text-sm font-['Manrope'] text-[#36454F]/75 hover:text-[#B76E79] hover:bg-[#B76E79]/5 rounded-sm transition-colors"
                data-testid="tab-case-files"
              >
                Case Files
              </Link>
            </nav>

            <div className="flex items-center space-x-4">
              <Link
                to="/profile"
                className="flex items-center text-[#36454F]/70 hover:text-[#36454F] transition-colors font-['Manrope']"
                data-testid="profile-link"
              >
                <User className="w-5 h-5 mr-1" strokeWidth={1.5} />
                <span className="hidden sm:inline">{user?.name || 'Profile'}</span>
              </Link>
              <Link
                to="/settings"
                className="flex items-center text-[#36454F]/70 hover:text-[#36454F] transition-colors"
                data-testid="settings-link"
              >
                <Settings className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-[#36454F]/70 hover:text-[#36454F] hover:bg-[#36454F]/5"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>

          {/* Mobile secondary bar — same tabs, tap-friendly */}
          <nav className="md:hidden flex items-center justify-around border-t border-[#36454F]/8 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2" data-testid="header-tabs-mobile">
            <Link to="/dashboard" className="text-xs font-['Manrope'] text-[#36454F]/70 hover:text-[#36454F] py-1 px-2" data-testid="tab-dashboard-mobile">Dashboard</Link>
            <Link to="/budget" className="text-xs font-['Manrope'] text-[#B76E79] py-1 px-2" data-testid="tab-family-budget-mobile">Family Budget</Link>
            <Link to="/case-files" className="text-xs font-['Manrope'] text-[#B76E79] py-1 px-2" data-testid="tab-case-files-mobile">Case Files</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Push reminder opt-in */}
        <PushReminderCard />
        {/* Welcome Section */}
        <div className="mb-6 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            Manage your credit disputes and track responses
          </p>
        </div>

        {/* Daily Verse Card */}
        <Card className="mb-6 border-[#E0BFB8] bg-[#E0BFB8]/5 animate-fade-in-up animation-delay-100">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <Heart className="w-6 h-6 text-[#B76E79] flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium mb-2">
                  Today's Encouragement
                </p>
                <p className="font-['Playfair_Display'] text-lg italic text-[#36454F]" data-testid="encouragement-verse">
                  "{verse.text}"
                </p>
                <p className="text-sm text-[#36454F]/50 font-['Manrope'] mt-1" data-testid="encouragement-ref">
                  — {verse.ref}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Install Prompt — shows only on mobile / non-installed PWA contexts */}
        <InstallPromptCard />

        {/* About Selah Slate — IS / IS NOT — collapsed by default so the dashboard breathes */}
        <details
          className="mb-8 rounded-sm border border-[#36454F]/10 bg-white animate-fade-in-up animation-delay-200 group"
          data-testid="about-selah-slate"
        >
          <summary
            className="p-5 cursor-pointer list-none flex items-center gap-4 hover:bg-[#36454F]/[0.02] rounded-sm"
            data-testid="about-selah-slate-summary"
          >
            <div className="w-10 h-10 rounded-sm bg-[#36454F]/5 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                About Selah Slate
              </h3>
              <p className="text-sm text-[#36454F]/60 font-['Manrope'] mt-0.5">
                What we are, what we are not — tap to expand.
              </p>
            </div>
            <ChevronRight
              className="w-5 h-5 text-[#36454F]/40 flex-shrink-0 transition-transform group-open:rotate-90"
              strokeWidth={1.5}
            />
          </summary>

          <div className="px-5 pb-5 pt-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-4 rounded-sm bg-[#4CAF50]/5 border border-[#4CAF50]/20">
                <p className="text-xs uppercase tracking-wider text-[#4CAF50] font-['Manrope'] font-semibold mb-2">
                  What we are
                </p>
                <ul className="space-y-1.5 text-sm text-[#36454F]/85 font-['Manrope']">
                  <li className="flex items-start gap-2">
                    <span className="text-[#4CAF50] mt-0.5">✓</span>
                    <span>An all-in-one educational companion — the app analyzes your credit report, drafts FCRA dispute letters, tracks bureau responses, syncs a shared household budget in real time, guides CFPB escalations, and plans debt payoff</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#4CAF50] mt-0.5">✓</span>
                    <span>Faith-based encouragement paired with practical steps — devotions, journal, testimony wall</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#4CAF50] mt-0.5">✓</span>
                    <span>Free, private, and ad-free — always</span>
                  </li>
                </ul>
              </div>
              <div className="p-4 rounded-sm bg-[#D9534F]/5 border border-[#D9534F]/20">
                <p className="text-xs uppercase tracking-wider text-[#D9534F] font-['Manrope'] font-semibold mb-2">
                  What we are not — and what we don't do
                </p>
                <ul className="space-y-1.5 text-sm text-[#36454F]/85 font-['Manrope']">
                  <li className="flex items-start gap-2">
                    <span className="text-[#D9534F] mt-0.5">✕</span>
                    <span>Not a credit repair company, lawyer, or financial advisor</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D9534F] mt-0.5">✕</span>
                    <span>Not a guarantee of any specific credit-score outcome</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D9534F] mt-0.5">✕</span>
                    <span>The app does not mail letters, negotiate with creditors, or contact bureaus on your behalf — you stay in control</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </details>

        {/* Financial Reflection — before-you-dispute check-in */}
        <Card
          className="mb-8 border-[#B76E79]/30 bg-gradient-to-br from-[#E0BFB8]/20 to-white animate-fade-in-up animation-delay-275"
          data-testid="reflection-card"
        >
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#B76E79]/15 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                    Before you dispute — pause &amp; reflect
                  </h3>
                  <p className="text-sm text-[#36454F]/65 font-['Manrope'] mt-0.5 max-w-xl">
                    A 5-minute, faith-rooted check-in on <em className="pr-1">why</em> the debt is there.
                    We'll pair your answers with scripture and 3 concrete next steps.
                  </p>
                </div>
              </div>
              <Link to="/reflection" data-testid="open-reflection-link">
                <Button className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm">
                  Start reflection
                  <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Print Station & Case Files — printable factual tracking ledgers */}
        <Card
          className="mb-8 border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-275"
          data-testid="case-files-card"
        >
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#B76E79]/12 flex items-center justify-center flex-shrink-0">
                  <Printer className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                    <span className="text-[10px] uppercase tracking-wider bg-[#B76E79] text-white px-2 py-0.5 rounded mr-2 align-middle font-['Manrope']">New</span>
                    Print Station &amp; Case Files
                  </h3>
                  <p className="text-sm text-[#36454F]/65 font-['Manrope'] mt-0.5 max-w-xl">
                    Download the printable factual tracking ledger — audit sheet, certified-mail
                    chain of custody, and the 30-day compliance calendar.
                  </p>
                </div>
              </div>
              <Link to="/case-files" data-testid="open-case-files-link">
                <Button className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm">
                  Open print station
                  <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Family Budget — shared envelope budgeting */}
        <Card
          className="mb-8 border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-275"
          data-testid="budget-card"
        >
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#36454F]/8 flex items-center justify-center flex-shrink-0">
                  <PieChart className="w-5 h-5 text-[#36454F]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                    <span className="text-[10px] uppercase tracking-wider bg-[#B76E79] text-white px-2 py-0.5 rounded mr-2 align-middle font-['Manrope']">New</span>
                    Family Budget
                  </h3>
                  <p className="text-sm text-[#36454F]/65 font-['Manrope'] mt-0.5 max-w-xl">
                    Budget together — envelopes, a live expense feed, and real-time sync with everyone
                    you invite.
                  </p>
                </div>
              </div>
              <Link to="/budget" data-testid="open-budget-link">
                <Button variant="outline" className="border-[#36454F]/25 text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm">
                  Open budget
                  <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Credit Mix Snapshot */}
        {creditMix && creditMix.total_accounts > 0 && (
          <Card
            className="mb-8 border-[#36454F]/10 bg-white animate-fade-in-up animation-delay-250"
            data-testid="credit-mix-card"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-[#B76E79]/10 flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                      Your Credit Mix
                    </h3>
                    <p className="text-xs text-[#36454F]/55 font-['Manrope'] mt-0.5">
                      {creditMix.total_accounts} account{creditMix.total_accounts === 1 ? '' : 's'} across your reports
                      {creditMix.negative_count > 0 && (
                        <>
                          {' · '}
                          <span className="text-[#D9534F]">{creditMix.negative_count} negative</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {creditMix.mix.map((m) => (
                  <MixTile key={m.type} type={m.type} count={m.count} />
                ))}
              </div>

              {creditMix.credit_card_balance_total > 0 && (
                <div className="mt-4 p-3 rounded-sm bg-[#F5F5F5] border border-[#36454F]/5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#36454F]/60" strokeWidth={1.5} />
                    <p className="text-sm text-[#36454F]/80 font-['Manrope']">
                      Revolving balance (all cards)
                    </p>
                  </div>
                  <p className="text-sm font-['Manrope'] font-semibold text-[#36454F]">
                    ${creditMix.credit_card_balance_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}

              {creditMix.tip && (
                <p className="mt-3 text-xs text-[#36454F]/65 font-['Manrope'] italic flex items-start gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  {creditMix.tip}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Profile Completion Alert */}
        {!profileComplete && (
          <div className="mb-8 animate-fade-in-up animation-delay-100">
            <Card className="border-[#E0BFB8] bg-[#E0BFB8]/10">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <AlertCircle className="w-6 h-6 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="flex-1">
                    <h3 className="font-['Manrope'] font-semibold text-[#36454F] mb-2">
                      Complete your profile to generate dispute letters
                    </h3>
                    <p className="text-sm text-[#36454F]/65 font-['Manrope'] mb-3">
                      We need your legal name, address, and last 4 digits of your SSN to create FCRA-compliant dispute letters.
                    </p>
                    <Progress value={profileProgress} className="h-2 mb-3 bg-[#36454F]/10" />
                    <Link to="/profile">
                      <Button 
                        size="sm" 
                        className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                        data-testid="complete-profile-btn"
                      >
                        Complete Profile
                        <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Upload Credit Report Card */}
          <Card className="md:col-span-2 border-[#36454F]/15 bg-white card-hover animate-fade-in-up animation-delay-100">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-2xl text-[#36454F] flex items-center">
                <Upload className="w-6 h-6 mr-3 text-[#B76E79]" strokeWidth={1.5} />
                Upload Credit Report
              </CardTitle>
              <CardDescription className="font-['Manrope'] text-[#36454F]/65">
                Upload your free{' '}
                <a
                  href="https://www.annualcreditreport.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[#B76E79] underline hover:text-[#9e5c66] font-medium"
                  data-testid="dashboard-acr-link"
                >
                  AnnualCreditReport.com
                </a>{' '}
                PDF to find and dispute errors
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/upload">
                <div 
                  className="upload-zone p-12 flex flex-col items-center justify-center cursor-pointer rounded-sm"
                  data-testid="upload-zone-link"
                >
                  <Upload className="w-12 h-12 text-[#B76E79]/70 mb-4" strokeWidth={1.5} />
                  <p className="text-[#36454F] font-['Manrope'] font-medium mb-1">
                    Click to upload your credit report
                  </p>
                  <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                    PDF files only • Encrypted & secure
                  </p>
                </div>
              </Link>
            </CardContent>
          </Card>

          {/* Quick Stats Card */}
          <Card className="border-[#36454F]/15 bg-white card-hover animate-fade-in-up animation-delay-200">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                <FileText className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                Your Disputes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-sm">
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-[#B76E79] mr-2" strokeWidth={1.5} />
                  <span className="font-['Manrope'] text-[#36454F]">Pending</span>
                </div>
                <span className="font-['Manrope'] font-semibold text-[#36454F]">{pendingDisputes.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-sm">
                <div className="flex items-center">
                  <Timer className="w-5 h-5 text-[#4CAF50] mr-2" strokeWidth={1.5} />
                  <span className="font-['Manrope'] text-[#36454F]">Tracking</span>
                </div>
                <span className="font-['Manrope'] font-semibold text-[#36454F]">{activeDisputes.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-sm">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-[#36454F]/50 mr-2" strokeWidth={1.5} />
                  <span className="font-['Manrope'] text-[#36454F]">Total</span>
                </div>
                <span className="font-['Manrope'] font-semibold text-[#36454F]">{disputes.length}</span>
              </div>
              {disputes.length > 0 && (
                <Link to="/disputes">
                  <Button 
                    variant="outline" 
                    className="w-full mt-4 border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm"
                    data-testid="view-disputes-btn"
                  >
                    View All Disputes
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Faith-Based Tools Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Link to="/debt-calculator" data-testid="debt-calculator-link">
            <Card className="border-[#36454F]/15 bg-white card-hover h-full animate-fade-in-up animation-delay-200">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-[#E0BFB8]/30 flex items-center justify-center mb-4">
                  <Calculator className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-1">
                  Debt Freedom Calculator
                </h3>
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                  Snowball vs Avalanche — find your fastest path to freedom
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/faith-strategies" data-testid="faith-strategies-link">
            <Card className="border-[#E0BFB8] bg-[#E0BFB8]/10 card-hover h-full animate-fade-in-up animation-delay-200">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-[#B76E79]/20 flex items-center justify-center mb-4">
                  <BookOpen className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-1">
                  Faith Strategies
                </h3>
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                  Biblical wisdom, devotionals, and prayers for your journey
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/account-timeline" data-testid="account-timeline-link">
            <Card className="border-[#36454F]/15 bg-white card-hover h-full animate-fade-in-up animation-delay-200">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-[#E0BFB8]/30 flex items-center justify-center mb-4">
                  <Calendar className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-1">
                  Account Timeline
                </h3>
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                  Track when negative items fall off your report
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/testimonies" data-testid="testimonies-link">
            <Card className="border-[#36454F]/15 bg-white card-hover h-full animate-fade-in-up animation-delay-200">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-[#E0BFB8]/30 flex items-center justify-center mb-4">
                  <MessageSquare className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-1">
                  Testimony Wall
                </h3>
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                  Stories of faith and financial victory
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/score-simulator" data-testid="score-simulator-link">
            <Card className="border-[#36454F]/15 bg-white card-hover h-full animate-fade-in-up animation-delay-200">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-[#E0BFB8]/30 flex items-center justify-center mb-4">
                  <TrendingUp className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-1">
                  Score Simulator
                </h3>
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                  Estimate your credit score improvement
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/journal" data-testid="journal-link">
            <Card className="border-[#36454F]/15 bg-white card-hover h-full animate-fade-in-up animation-delay-200">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-[#E0BFB8]/30 flex items-center justify-center mb-4">
                  <PenLine className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-1">
                  Progress Journal
                </h3>
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                  Track milestones on your journey
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Share My Progress Card */}
        {disputes.length > 0 && (
          <Card
            className="mb-8 border-[#36454F]/15 bg-gradient-to-br from-[#36454F] to-[#4a5a66] text-white animate-fade-in-up animation-delay-300"
            data-testid="share-progress-card"
          >
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-sm bg-[#B76E79] flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl font-semibold mb-1">
                      Share your progress
                    </h3>
                    <p className="text-sm text-white/75 font-['Manrope'] max-w-md">
                      {itemsCleared > 0
                        ? `${itemsCleared} item${itemsCleared === 1 ? '' : 's'} cleared. `
                        : ''}
                      Celebrate the journey — encourage someone else on theirs. No account details are shared.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShareOpen(true)}
                  className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm flex-shrink-0"
                  data-testid="open-share-progress-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Share My Progress
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Disputes with Timers */}
        {activeDisputes.length > 0 && (
          <div className="mb-8 animate-fade-in-up animation-delay-300">
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-4">
              Response Tracking
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeDisputes.map((dispute) => (
                <Card key={dispute.dispute_id} className="border-[#36454F]/15 bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope']">
                        {dispute.bureau}
                      </span>
                      <span className="text-xs px-2 py-1 bg-[#4CAF50]/10 text-[#4CAF50] rounded font-['Manrope']">
                        Mailed
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            fill="none"
                            stroke="rgba(54, 69, 79, 0.1)"
                            strokeWidth="4"
                          />
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            fill="none"
                            stroke="#B76E79"
                            strokeWidth="4"
                            strokeDasharray={`${(dispute.days_remaining / 35) * 176} 176`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="font-['Manrope'] font-semibold text-[#36454F]">
                            {dispute.days_remaining}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="font-['Manrope'] font-medium text-[#36454F]">
                          Days remaining
                        </p>
                        <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                          {dispute.items?.length || 0} items disputed
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#36454F]/65 font-['Manrope']">Loading your disputes...</p>
          </div>
        ) : disputes.length === 0 && (
          <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-300">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-[#36454F]/20 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">
                No disputes yet
              </h3>
              <p className="text-[#36454F]/65 font-['Manrope'] mb-6 max-w-md mx-auto">
                Upload your credit report to identify errors and generate professional dispute letters.
              </p>
              <Link to="/upload">
                <Button 
                  className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                  data-testid="start-upload-btn"
                >
                  Upload Credit Report
                  <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </main>
      <AppFooter />
      <ShareProgressCard
        open={shareOpen}
        onOpenChange={setShareOpen}
        stats={shareStats}
        userName={user?.name}
      />
    </div>
  );
}

const MIX_ICON = {
  'Credit Card': CreditCard,
  'Auto Loan': Car,
  'Mortgage': Home,
  'Student Loan': GraduationCap,
  'Personal Loan': Banknote,
  'Installment': Banknote,
  'Collection': AlertTriangle,
  'Public Record': FileWarning,
  'Inquiry': Search,
  'Other': HelpCircle,
};

const MIX_TONE = {
  'Collection': 'bg-[#D9534F]/5 border-[#D9534F]/20 text-[#D9534F]',
  'Public Record': 'bg-[#D9534F]/5 border-[#D9534F]/20 text-[#D9534F]',
};

function MixTile({ type, count }) {
  const Icon = MIX_ICON[type] || HelpCircle;
  const tone = MIX_TONE[type] || 'bg-[#F5F5F5] border-[#36454F]/10 text-[#36454F]';
  return (
    <div
      className={`p-3 rounded-sm border ${tone} flex items-center gap-3`}
      data-testid={`mix-tile-${type.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-8 h-8 rounded-sm bg-white/60 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wider font-['Manrope'] opacity-70 truncate">
          {type}
        </p>
        <p className="text-xl font-['Playfair_Display'] font-semibold text-[#36454F] leading-tight">
          {count}
        </p>
      </div>
    </div>
  );
}
